import { Group, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { optInGqlx } from "shared/api/gqlx";
import { useOptInUserFormContext } from "shared/components/opt-in/context";
import { UserSubscribeTopicNode } from "shared/components/organization/topics/UserSubscribeTopicNode";
import { useFormState } from "shared/hooks/useFormState";
import { useOptInUserMutation } from "shared/hooks/useGraphql";
import { fail } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";

export const TopicsPanel = () => {
    const form = useOptInUserFormContext();
    const [customerId] = useFormState(form, "customer.id");
    const [userId] = useFormState(form, "id");
    const [topics] = useFormState(form, "topics");

    const [isLoading, setIsLoading] = useState<number>(-1);

    const subToTopic = useOptInUserMutation(
        graphql(`
            mutation OptInSubToTopic($userId: UUID!, $topicId: UUID!) {
                addUserToTopic(userId: $userId, topicId: $topicId) {
                    ...TopicLike
                }
            }
        `),
    );
    const unsubFromTopic = useOptInUserMutation(
        graphql(`
            mutation OptInUnsubFromTopic($userId: UUID!, $topicId: UUID!) {
                removeUserFromTopic(userId: $userId, topicId: $topicId) {
                    ...TopicLike
                }
            }
        `),
    );

    return (
        <Stack>
            <Group>
                <Title order={4}>Topic Subscriptions</Title>
                {(subToTopic.isPending || unsubFromTopic.isPending || isLoading > 0) && (
                    <Text fs="italic" fz="sm">
                        Saving...
                    </Text>
                )}
            </Group>
            <Stack gap={0}>
                <Text>Subscribe to the topics to receive alerts on that subject.</Text>
                <Text fw={600}>Note: you cannot unsubscribe from required topics.</Text>
            </Stack>
            <Stack gap={0}>
                <UserSubscribeTopicNode
                    transport={optInGqlx(userId)}
                    defaultCheckedTopics={topics.filter((it) => !it.isRequired) ?? []}
                    checkboxOnChange={(topic, checked) => {
                        if (!userId) return fail("Opt-in ID not found");
                        clearTimeout(isLoading);
                        setIsLoading(+setTimeout(() => setIsLoading(-1), 500));

                        if (checked) {
                            subToTopic
                                .mutateAsync({ userId, topicId: topic.id })
                                .then(({ addUserToTopic: res }) =>
                                    form.setFieldValue("topics", (prev) =>
                                        prev.concat({ ...res, description: res.description ?? undefined, parent: res.parent ?? undefined }),
                                    ),
                                )
                                .catch(notify.catch);
                        } else {
                            unsubFromTopic
                                .mutateAsync({ userId, topicId: topic.id })
                                .then(() => form.setFieldValue("topics", (prev) => prev.filter((it) => it.id != topic.id)))
                                .catch(notify.catch);
                        }
                    }}
                    customerId={customerId}
                />
            </Stack>
        </Stack>
    );
};
